import store from 'store';
import history from 'store/history';
import { newOlsUrl } from '../axiosBaseUrls/index.js';

newOlsUrl.interceptors.response.use(
  response => response,
  error => {
    const status = error.response ? error.response.status : null;

    if (status === 401 || status === 403) {
      if (
        history.location.pathname !== '/login' &&
        history.location.pathname !== '/new_password' &&
        history.location.pathname !== '/reset_password'
      ) {
        store.dispatch({ type: 'LOGOUT_REQUEST' });
      }
    }

    return Promise.reject(error);
  }
);

// Generate signature for API requests
const generateSignature = async (method, endpoint, timestamp) => {
  try {
    const key = process.env.REACT_APP_AUTH_KEY;

    // Create canonical string: RequestType:EndpointPath:Timestamp
    const canonicalString = `${method}:${endpoint}:${timestamp}`;

    // The key is base64 encoded, decode it first
    const keyBytes = Uint8Array.from(atob(key), c => c.charCodeAt(0));
    const messageBytes = new TextEncoder().encode(canonicalString);

    const importedKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'HMAC', hash: { name: 'SHA-256' } }, // Using SHA-256
      false,
      ['sign']
    );

    const signatureBuffer = await crypto.subtle.sign('HMAC', importedKey, messageBytes);
    const signatureArray = Array.from(new Uint8Array(signatureBuffer));
    const hmac = signatureArray.map(byte => byte.toString(16).padStart(2, '0')).join('');

    return hmac.toUpperCase();
  } catch (error) {
    console.error('Error creating signature:', error);
    return null;
  }
};

// Add signature and timestamp headers to requests
newOlsUrl.interceptors.request.use(async config => {
  // Generate ISO 8601 UTC timestamp
  const timestamp = new Date().toISOString();

  // Get the endpoint path from the URL without query parameters
  const url = config.url.split('?')[0];

  // Get the request method
  const method = config.method.toUpperCase();

  // Generate the signature
  const signature = await generateSignature(method, url, timestamp);

  if (signature) {
    // Add headers
    config.headers['X-Timestamp'] = timestamp;
    config.headers['X-OLS-Signature'] = signature;
  }

  return config;
});

const setHeaders = headers => {
  const defaultHeader = { 'Content-Type': 'application/json' };
  if (headers) return Object.assign(defaultHeader, headers);
  return defaultHeader;
};

const setAuthTokenToHeader = token => {
  if (token) {
    newOlsUrl.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete newOlsUrl.defaults.headers.common.Authorization;
  }
};

const get = (url, headers) => {
  return newOlsUrl.get(`${url}`, setHeaders(headers));
};

const post = (url, data, headers) => {
  const currentHeaders = setHeaders(headers);
  return newOlsUrl.post(`${url}`, data, currentHeaders);
};

const patch = (url, data, headers) => {
  return newOlsUrl.patch(`${url}`, data, setHeaders(headers));
};

const put = (url, data, headers) => {
  return newOlsUrl.put(`${url}`, data, setHeaders(headers));
};

const remove = (url, data, headers) => {
  return newOlsUrl.delete(`${url}`, setHeaders(headers));
};

export default {
  get,
  post,
  put,
  patch,
  remove,
  setAuthTokenToHeader,
};
